import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { NoteImportModel } from 'src/app/models/imports/note-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { NotePrintModel } from 'src/app/models/invoicing/note-print.model';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { environment } from 'src/environments/environment';
import { NoteModel } from './../../models/invoicing/note.model';

@Injectable({
  providedIn: 'root'
})
export class NoteService {

  constructor(private _httpClient: HttpClient) { }

  annulInvoiceByNote(noteId: number): Observable<NoteModel> {
    return this._httpClient.put<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.AnnulInvoiceByNote}/${noteId}`,
      null);
  }

  createNote(noteContract: NoteModel): Observable<NoteModel> {
    return this._httpClient.post<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.CreateNote}`,
      noteContract);
  }

  deleteNote(noteId: number): Observable<NoteModel> {
    return this._httpClient.delete<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.DeleteNote}/${noteId}`);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.note.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean, isDocumentSupport: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString()).set('isDocumentSupport', isDocumentSupport);
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  generateNoteByRequestNoteAsync(noteId: number): Observable<NoteModel> {
    return this._httpClient.post<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GenerateNoteByRequestNoteAsync}/${noteId}`,
      null);
  }

  getAccountingInterfaceCode(accountingInterfaceId: number): Observable<string> {
    return this._httpClient.get<{ value: string }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetAccountingInterfaceCodeAsync}/${accountingInterfaceId}`)
      .pipe(map(response => response.value));
  }

  getAllBussinessDocumentsTypesForSelectAsync(bussinessDocumentsClassId: number, branchId: number, enterpriseId: number): Observable<Array<any>> {
    const params = new HttpParams().set('bussinessDocumentsClassId', bussinessDocumentsClassId).set('branchId', branchId).set('enterpriseId', enterpriseId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetAllBussinessDocumentsTypesForSelectAsync}`,
      { params: params });
  }

  getAllBussinessDocumentsTypesInvoiceForSelectAsync(isDocumentSupport: boolean, branchId: number, enterpriseId: number): Observable<Array<any>> {
    const params = new HttpParams().set('isDocumentSupport', isDocumentSupport).set('branchId', branchId).set('enterpriseId', enterpriseId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetAllBussinessDocumentsTypesInvoiceForSelectAsync}`,
      { params: params });
  }

  getAllConceptForSelectAsync(typeConcept: number): Observable<Array<any>> {
    const params = new HttpParams().set('typeConcept', typeConcept);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetAllConceptForSelectAsync}`,
      { params: params }
    );
  }

  getAllNoteTypeForSelectsAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetAllNoteTypeForSelectsAsync}`,);
  }

  getNotesWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], isDocumentSupport: boolean): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('isDocumentSupport', isDocumentSupport);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetAllNotesWithPagination}`,
      filters, { params: params }
    );
  }

  getClientByIdForRegisterNoteAsync(clientId: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetClientByIdForRegisterNoteAsync}/${clientId}`,);
  }

  getColumnsForGeneralConsultation(isDocumentSupport: boolean): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetColumnsForGeneralConsultation}/${isDocumentSupport}`);
  }

  getElectronicInvoiceInterfaceCodeById(electronicInvoiceInterfaceId: number): Observable<string> {
    return this._httpClient.get<{ value: string }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetElectronicInvoiceInterfaceCodeByIdAsync}/${electronicInvoiceInterfaceId}`)
      .pipe(map(response => response.value));
  }

  getFirstNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number): Observable<NoteModel> {
    const params = new HttpParams().set('branchId', branchId).set('enterpriseId', enterpriseId).set('bussinessDocumentsTypeId', bussinessDocumentsTypeId).set('noteTypeId', noteTypeId);
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetFirstNoteByKeys}`, { params: params });
  }

  getLastNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number): Observable<NoteModel> {
    const params = new HttpParams().set('branchId', branchId).set('enterpriseId', enterpriseId).set('bussinessDocumentsTypeId', bussinessDocumentsTypeId).set('noteTypeId', noteTypeId);
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetLastNoteByKeys}`, { params: params });
  }

  getLastNoteForFormats(branchId: number, enterpriseId: number): Observable<NoteModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetLastNoteForFormats}`, { params: params });
  }

  getNextNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number,
    consecutive: number | null): Observable<NoteModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    params = params.set('noteTypeId', noteTypeId);
    if (consecutive !== null) params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetNextNoteByKeys}`, { params: params });
  }

  getNoteById(noteId: number): Observable<NoteModel> {
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetNoteById}/${noteId}`);
  }

  getNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number,
    consecutive: number, isDocumentSupport: boolean): Observable<NoteModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    params = params.set('noteTypeId', noteTypeId);
    if (consecutive !== null) params = params.set('consecutive', consecutive.toString());
    params = params.set('isDocumentSupport', isDocumentSupport);
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetNoteByKeys}`, { params: params });
  }

  getPermissionPrint(): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetPermissionPrint}`);
  }

  getPreviusNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number,
    consecutive: number): Observable<NoteModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    params = params.set('noteTypeId', noteTypeId);
    if (consecutive !== null) params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetPreviusNoteByKeys}`, { params: params });
  }

  getProviderByIdForRegisterNoteAsync(providerId: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.GetProviderByIdForRegisterNoteAsync}/${providerId}`,);
  }

  importNotesAsync(noteConceptImports: Array<NoteImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.ImportNotesAsync}`,
      noteConceptImports);
  }

  printNote(noteId: number, print: boolean): Observable<NotePrintModel> {
    let params = new HttpParams()
    params = params.set('print', print);
    return this._httpClient.get<NotePrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.PrintNote}/${noteId}`, { params: params });
  }

  setStateNote(noteId: number): Observable<NoteModel> {
    return this._httpClient.put<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.SetNoteState}/${noteId}`,
      null);
  }

  updateNote(noteContract: NoteModel): Observable<NoteModel> {
    return this._httpClient.put<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.note.UpdateNote}`,
      noteContract);
  }
}
